import { z } from "zod";

const PERCENT_SUM_TOLERANCE = 0.01;

export const percentField = z
  .number({ invalid_type_error: "Informe um número válido" })
  .min(0, "O valor mínimo é 0%")
  .max(100, "O valor máximo é 100%");

export const integerField = z
  .coerce.number({ invalid_type_error: "Informe um número válido" })
  .refine((value) => !Number.isNaN(value), "Informe um número válido");

const baseSchema = z.object({
  name: z.string().min(1, "Informe um nome"),
  description: z.string().optional(),
  trafficPercentage: percentField,
  trafficMobileClicksPercentage: percentField,
  trafficWebClicksPercentage: percentField,
  trafficConversionClicksPercentage: percentField,
  averageTrafficPercentage: percentField,
  averageTrafficMobileClicksPercentage: percentField,
  averageTrafficWebClicksPercentage: percentField,
  averageTrafficConversionClicksPercentage: percentField,
  monthQuantityForAverage: integerField.transform((value) => Math.round(value)),
  presetUsePercentage: percentField,
});

export const sumEqualsHundred = (values: number[]) =>
  Math.abs(values.reduce((acc, value) => acc + value, 0) - 100) <= PERCENT_SUM_TOLERANCE;

export const shareConfigFormSchema = baseSchema.superRefine((data, ctx) => {
  // Validação dos subvalores de traffic_percentage (só valida se traffic_percentage > 0)
  if (data.trafficPercentage > 0) {
    const trafficSubValues = [
      data.trafficMobileClicksPercentage,
      data.trafficWebClicksPercentage,
      data.trafficConversionClicksPercentage,
    ];
    if (!sumEqualsHundred(trafficSubValues)) {
      [
        "trafficMobileClicksPercentage",
        "trafficWebClicksPercentage",
        "trafficConversionClicksPercentage",
      ].forEach((field) => {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "A soma dos subvalores de traffic_percentage deve ser 100%",
          path: [field],
        });
      });
    }
  }

  // Validação dos subvalores de average_traffic_percentage (só valida se average_traffic_percentage > 0)
  if (data.averageTrafficPercentage > 0) {
    const averageSubValues = [
      data.averageTrafficMobileClicksPercentage,
      data.averageTrafficWebClicksPercentage,
      data.averageTrafficConversionClicksPercentage,
    ];
    if (!sumEqualsHundred(averageSubValues)) {
      [
        "averageTrafficMobileClicksPercentage",
        "averageTrafficWebClicksPercentage",
        "averageTrafficConversionClicksPercentage",
      ].forEach((field) => {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "A soma dos subvalores de average_traffic_percentage deve ser 100%",
          path: [field],
        });
      });
    }
  }

  // Validação: preset_use_percentage + traffic_percentage + average_traffic_percentage = 100%
  const mainPercentages = [
    data.presetUsePercentage,
    data.trafficPercentage,
    data.averageTrafficPercentage,
  ];
  if (!sumEqualsHundred(mainPercentages)) {
    ["presetUsePercentage", "trafficPercentage", "averageTrafficPercentage"].forEach((field) => {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "A soma de preset_use_percentage + traffic_percentage + average_traffic_percentage deve ser 100%",
        path: [field],
      });
    });
  }
});

export type ShareConfigFormValues = z.infer<typeof shareConfigFormSchema>;

export const shareConfigDefaultValues: ShareConfigFormValues = {
  name: "",
  description: "",
  trafficPercentage: 0,
  trafficMobileClicksPercentage: 0,
  trafficWebClicksPercentage: 0,
  trafficConversionClicksPercentage: 0,
  averageTrafficPercentage: 0,
  averageTrafficMobileClicksPercentage: 0,
  averageTrafficWebClicksPercentage: 0,
  averageTrafficConversionClicksPercentage: 0,
  monthQuantityForAverage: 1,
  presetUsePercentage: 0,
};

